import React from 'react';

import { Info as InfoIcon, PushPin as PinIcon, PushPinOutlined as UnpinIcon } from '@mui/icons-material';
import { Button, Paper, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';

interface RowPinningSectionProps {
  selectedRowCount: number;
  pinnedTopCount: number;
  pinnedBottomCount: number;
  onPinToTop: () => void;
  onPinToBottom: () => void;
  onUnpinAll: () => void;
  themeColor: string;
}

export default function RowPinningSection({
  selectedRowCount,
  pinnedTopCount,
  pinnedBottomCount,
  onPinToTop,
  onPinToBottom,
  onUnpinAll,
  themeColor,
}: RowPinningSectionProps) {
  const theme = useTheme();
  const totalPinned = pinnedTopCount + pinnedBottomCount;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: theme.typography.fontWeightBold }}>
            Row Pinning:
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PinIcon />}
            onClick={onPinToTop}
            disabled={selectedRowCount === 0}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              borderColor: themeColor,
              color: themeColor,
              '&:hover': {
                borderColor: themeColor,
                bgcolor: alpha(themeColor, 0.1),
              },
            }}
          >
            Pin to Top ({selectedRowCount})
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PinIcon sx={{ transform: 'rotate(180deg)' }} />}
            onClick={onPinToBottom}
            disabled={selectedRowCount === 0}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              borderColor: themeColor,
              color: themeColor,
              '&:hover': {
                borderColor: themeColor,
                bgcolor: alpha(themeColor, 0.1),
              },
            }}
          >
            Pin to Bottom ({selectedRowCount})
          </Button>
          <Button
            variant="text"
            size="small"
            startIcon={<UnpinIcon />}
            onClick={onUnpinAll}
            disabled={totalPinned === 0}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              color: themeColor,
              '&:hover': {
                bgcolor: alpha(themeColor, 0.1),
              },
            }}
          >
            Unpin All ({totalPinned})
          </Button>
        </Stack>

        {/* Right side: Quick tips */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip
            title="Select rows using checkboxes, then use the buttons to pin them to top or bottom of the table. Pinned rows stay visible across pagination."
            arrow
            placement="left"
          >
            <InfoIcon
              sx={{
                fontSize: theme.typography.body2.fontSize,
                color: 'text.secondary',
                cursor: 'help',
              }}
            />
          </Tooltip>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: { xs: 'none', md: 'block' },
            }}
          >
            Select rows to pin them across pages
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}
